import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheckIcon,
  IdentificationCardIcon,
  HandshakeIcon,
  EyeIcon,
  TrophyIcon,
  GavelIcon,
} from "@phosphor-icons/react";

/**
 * FounderLetter — the canonical body content of the founder's letter.
 *
 * Used in three places:
 *   • /letter  — standalone shareable page (FounderLetterPage)
 *   • /why-bidorah — embedded as the closing section
 *   • Landing — short excerpt with a "Read full letter →" CTA
 *
 * Renders ONLY the letter content. Navbar + Footer are owned by the
 * containing route so the same body works inside any layout.
 *
 * Props:
 *   variant  — "full" (default) renders the entire letter.
 *              "excerpt" renders a teaser block for the landing page.
 */
export default function FounderLetter({ variant = "full" }) {
  if (variant === "excerpt") return <FounderLetterExcerpt />;
  return <FounderLetterFull />;
}

function FounderLetterFull() {
  return (
    <article
      className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20 text-zinc-800 [&>p]:my-5 [&>p]:leading-relaxed [&>p]:text-base sm:[&>p]:text-lg [&>ul>li]:leading-relaxed"
      data-testid="founder-letter-full"
    >
      <header className="mb-10">
        <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#1D78AB] mb-3">
          From the founder · Bidorah
        </div>
        {/* Founder portrait — circular avatar above the headline. The
            photo is the single biggest trust signal on this page; we lead
            with it so visitors put a face to the words immediately. */}
        <div className="mb-6">
          <div className="relative inline-block">
            <img
              src="/founder/noel-campos-720.jpg"
              srcSet="/founder/noel-campos-320.jpg 320w, /founder/noel-campos-720.jpg 720w"
              sizes="(max-width: 640px) 176px, 240px"
              alt="Noel Campos, Founder & CEO of Bidorah"
              className="h-44 w-44 sm:h-60 sm:w-60 rounded-full object-cover ring-4 ring-amber-200/70 shadow-2xl shadow-amber-200/30"
              style={{ objectPosition: "center 22%" }}
              data-testid="founder-letter-portrait"
            />
            <div className="absolute -bottom-1 -right-1 h-10 w-10 sm:h-14 sm:w-14 rounded-full bg-amber-400 grid place-items-center ring-4 ring-white shadow-lg">
              <TrophyIcon size={22} weight="fill" className="text-amber-950" />
            </div>
          </div>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-zinc-950">
          A note from the founder.
        </h1>
        <div className="mt-4 text-sm text-zinc-500">
          On building a safer, fairer, more rewarding live marketplace —
          from one person's laptop to a community that actually scales.
        </div>
      </header>

      <p className="!text-lg sm:!text-xl !leading-relaxed text-zinc-700">
        Bidorah was built on a single conviction: that the most valuable
        communities online are the ones that are <em>real</em>. Real
        people, real items, real bids — and a place where collectors,
        hobbyists, side-hustles, and serious operators can all find a
        stage that matches their ambition.
      </p>

      {/* "Team of one" block — gets prominent visual treatment because
          it's a powerful authenticity signal. */}
      <aside
        className="my-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-amber-50 via-amber-100/60 to-white border border-amber-200/70 shadow-sm"
        data-testid="founder-letter-team-of-one"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-amber-400 grid place-items-center text-amber-950">
            <TrophyIcon size={24} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-amber-900 mb-1.5">
              The transparent truth
            </div>
            <p className="text-base sm:text-lg font-bold leading-snug text-zinc-950 mb-2">
              Bidorah is being built by one person.
            </p>
            <p className="text-sm sm:text-base leading-relaxed text-zinc-700">
              Every line of code, every product decision, every late-night
              seller question — handled by a single founder with a laptop,
              an obsession with this category, and a refusal to ship
              anything that isn't excellent. Where other platforms have
              hundreds to thousands of people maintaining the status quo,
              Bidorah has one person rebuilding the next level of shopping
              from first principles. That's not a limitation. That's the
              edge.
            </p>
          </div>
        </div>
      </aside>

      <p>
        I launched Bidorah for everyone who has ever wanted live commerce
        to feel safer, fairer, and more rewarding than it has been.
        Whether you're a sneakerhead grading every PSA card by hand, a
        crystal collector looking to scale, a Storage-Unit Finds
        reseller, or a craft maker building a brand from your kitchen
        table — this platform is built around you, not on top of you.
      </p>

      <p>
        In our first chapter we have already welcomed verified sellers
        and buyers across categories that are notoriously hard to do well
        — from luxury handbags and vintage watches to pokémon, sports
        cards, reptiles, and beyond. None of this would have happened
        without the early believers who took a chance on Bidorah. Thank
        you. You are the reason this exists.
      </p>

      <h2 className="font-display font-black text-3xl sm:text-4xl mt-14 mb-2 tracking-tight text-zinc-950">
        Where we differ — and why it matters.
      </h2>
      <p className="text-zinc-600">
        A marketplace is only as good as its weakest checkpoint. Bidorah
        was built differently from the ground up.
      </p>

      {/* ID verification — expanded protection list */}
      <div
        className="mt-10 p-6 sm:p-8 rounded-3xl bg-zinc-950 text-zinc-100 shadow-xl"
        data-testid="founder-letter-id-section"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-[#1D78AB] grid place-items-center">
            <IdentificationCardIcon size={20} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#5fb1d8]">
              Trust layer 01
            </div>
            <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight">
              U.S. government photo-ID verification — required for every
              seller and every high-value buyer.
            </h3>
          </div>
        </div>
        <p className="text-sm sm:text-base text-zinc-300 mb-5 leading-relaxed">
          The cost is two minutes during onboarding. The benefit is a
          marketplace where you can spend $5 or $5,000 with the same
          confidence. Here's exactly what that protects you from:
        </p>
        <ul className="space-y-3 text-sm sm:text-base">
          {ID_PROTECTIONS.map((p) => (
            <li key={p.title} className="flex items-start gap-3">
              <ShieldCheckIcon
                size={18}
                weight="fill"
                className="flex-shrink-0 mt-0.5 text-[#5fb1d8]"
              />
              <div>
                <span className="font-bold text-white">{p.title}</span>
                <span className="text-zinc-300"> — {p.body}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <h3 className="font-display font-black text-xl sm:text-2xl mt-12 mb-3 tracking-tight">
        Other commitments to the community.
      </h3>
      <ul className="space-y-3 my-6">
        <li className="flex items-start gap-3">
          <HandshakeIcon
            size={18}
            weight="fill"
            className="flex-shrink-0 mt-1 text-[#1D78AB]"
          />
          <span>
            <strong>Wholesale and retail are strictly segregated.</strong>{" "}
            A wholesale buyer must submit a Reseller Permit, TIN, and
            registered Business Entity — and an admin must approve it —
            before they can bid on or purchase a B2B listing. Wholesale
            sellers cannot, and will not, sell to retail accounts. That
            protects margins on both sides of the table.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <EyeIcon
            size={18}
            weight="fill"
            className="flex-shrink-0 mt-1 text-[#1D78AB]"
          />
          <span>
            <strong>Real WebRTC live video</strong> with optional LiveKit
            SFU — 4K streams stay sharp even when your crowd hits four
            digits.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <GavelIcon
            size={18}
            weight="fill"
            className="flex-shrink-0 mt-1 text-[#1D78AB]"
          />
          <span>
            <strong>Bidoria</strong>, our live language + voice engine,
            brings the energy of a seasoned auctioneer to every show, on
            demand, in nine personalities — a tool to help you scale, not
            replace you.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <ShieldCheckIcon
            size={18}
            weight="fill"
            className="flex-shrink-0 mt-1 text-[#1D78AB]"
          />
          <span>
            <strong>Escrow-backed payouts</strong> through Stripe and
            PayPal. No surprises, no chargeback roulette.
          </span>
        </li>
      </ul>

      <h2 className="font-display font-black text-3xl sm:text-4xl mt-14 mb-3 tracking-tight text-zinc-950">
        The revolution is the next level.
      </h2>
      <p>
        Live commerce as it exists today is a starter floor. Bidorah is
        the building. The mission is to give every honest seller — whether
        you're shipping ten orders a month or ten thousand — the tools to
        scale into a real, durable business: real revenue, real
        reputation, real protection. I am pouring my energy into making
        this the safest, most credible, and most rewarding live
        marketplace on the internet. Not a feed. Not a flea market. A
        platform built to last — and built to be worthy of the people on
        it.
      </p>

      <p>
        To every founder seller, every collector, every wholesale buyer,
        every reptile rescuer, every coin grader, every storage-unit
        hunter, every artisan, and every curious first-time bidder
        reading this: you are why I am here. Keep building, keep showing
        up, keep raising the bar. I will keep meeting you there.
      </p>

      <div
        className="mt-12 p-6 sm:p-8 rounded-3xl bg-zinc-50 border border-zinc-200"
        data-testid="founder-letter-contact"
      >
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-base font-bold leading-snug text-zinc-950 mb-1">
              Keep building with us.
            </p>
            <p className="text-sm text-zinc-600">
              Every founder seller, buyer, and curious first-timer on Bidorah
              shapes what this platform becomes next.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center gap-3">
        <img
          src="/founder/noel-campos-320.jpg"
          alt="Noel Campos"
          className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-200/60"
          style={{ objectPosition: "center 22%" }}
          data-testid="founder-letter-signoff-avatar"
        />
        <div className="text-sm leading-tight">
          <div className="font-bold text-zinc-950">Noel Campos</div>
          <div className="text-zinc-600">Founder &amp; CEO · Bidorah</div>
        </div>
      </div>
    </article>
  );
}

function FounderLetterExcerpt() {
  return (
    <section
      className="bg-gradient-to-b from-white to-amber-50/40 border-y border-amber-100/60"
      data-testid="founder-letter-excerpt"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid md:grid-cols-[auto_1fr] gap-8 sm:gap-12 items-start">
          <div className="flex-shrink-0">
            <div className="relative inline-block">
              <img
                src="/founder/noel-campos-320.jpg"
                alt="Noel Campos, Founder & CEO of Bidorah"
                className="h-32 w-32 sm:h-44 sm:w-44 rounded-full object-cover ring-4 ring-amber-200/60 shadow-xl shadow-amber-200/40"
                style={{ objectPosition: "center 22%" }}
                data-testid="founder-letter-excerpt-avatar"
              />
              <div className="absolute -bottom-1 -right-1 h-10 w-10 rounded-full bg-amber-400 grid place-items-center ring-4 ring-white shadow">
                <TrophyIcon size={16} weight="fill" className="text-amber-950" />
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#1D78AB] mb-3">
              From the founder
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-zinc-950 mb-5">
              Built by one person. <br className="hidden sm:block" />
              Built for everyone who shows up real.
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-zinc-700 mb-6 max-w-2xl">
              Every line of code, every product decision, every late-night
              seller question — handled by a single founder. Where other
              platforms have hundreds to thousands of people maintaining
              the status quo, Bidorah has one person rebuilding the next
              level of shopping from first principles. <strong>U.S.
              ID-verified sellers + buyers, strict wholesale gating,
              escrow-backed payouts</strong> — built so you can spend $5
              or $5,000 with the same confidence.
            </p>
            <Link
              to="/letter"
              className="inline-flex items-center gap-2 rounded-full bg-zinc-950 hover:bg-zinc-800 text-white font-bold text-sm px-6 py-3 transition"
              data-testid="founder-letter-read-full-cta"
            >
              Read the founder's letter →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const ID_PROTECTIONS = [
  {
    title: "No bot-farms or burner accounts running live auctions.",
    body:
      "Every host on Bidorah is a real, identifiable person whose legal identity has been matched to a U.S. government-issued ID. If someone scams you, we know who they are — so do the authorities.",
  },
  {
    title: "No fraud rings hiding behind fake names.",
    body:
      "Repeat scammers can't simply spin up new anonymous accounts. Once an ID is flagged, it's blocked across the platform forever.",
  },
  {
    title: "No money-laundering, no sanctions evasion, no minor onboarding.",
    body:
      "ID verification means we comply with KYC/AML standards out of the gate, which keeps the entire platform — and your transactions — clean.",
  },
  {
    title: "Sellers earn real reputations.",
    body:
      "When a seller's identity is verified and tied to their entire history, the trust score on a Founder Seller badge actually means something. You're not buying from @xx_resell_xx_2024. You're buying from a verified human who's accountable.",
  },
  {
    title: "Buyers get real recourse.",
    body:
      "If a dispute escalates, escrow-backed payouts and verified-identity records mean refunds, chargebacks, and legal follow-through actually work. No anonymous seller can disappear with your money.",
  },
  {
    title: "Predators and harassers can't hide.",
    body:
      "Every chat message, every bid, every stream is tied to a real identity. Toxic behavior gets the offender banned permanently — not just from one alias, but from the platform.",
  },
];
